import React, { useRef } from 'react';
import { Link } from 'react-router-dom';
import { Linkedin, ChevronRight } from 'lucide-react';
import { useParallax } from '../../hooks/useScrollFx';

/**
 * SoulSection — split-screen immersive section with a full-bleed parallax image
 * on one side and a text block on the other. Configurable image side,
 * background, filter, object-position and parallax strength.
 */
export default function SoulSection({
  id,
  eyebrow,
  title,
  subtitle,
  subtitleIcon,
  body,
  quote,
  image,
  imageAlt,
  imageLeft = false,
  background,
  imageFilter,
  imageObjectPosition = 'center center',
  parallaxOffset = 0.18,
  parallaxStretch = 0.15,
  linkedin,
  linkedinLabel = 'Raffaello Tiezzi su LinkedIn',
  storyLink,
  storyLinkLabel,
  onStoryClick,
  imageSlot,
}) {
  const sectionRef = useRef(null);
  const imgRef = useParallax(parallaxOffset, sectionRef);

  return (
    <section
      id={id}
      ref={sectionRef}
      className="relative w-full"
      style={{ background, minHeight: '92vh' }}
    >
      <div className="grid grid-cols-1 md:grid-cols-2 items-stretch" style={{ minHeight: '92vh' }}>
        {/* Image side — full bleed with parallax (skipped when imageSlot is used) */}
        <div
          className={`relative overflow-hidden reveal ${imageLeft ? 'md:order-1' : 'md:order-2'}`}
          style={{ minHeight: '60vh' }}
        >
          {imageSlot ? (
            <div className="absolute inset-0">{imageSlot}</div>
          ) : (
            <div
              ref={imgRef}
              className="parallax"
              style={{
                position: 'absolute',
                top: `-${parallaxStretch * 100}%`,
                left: 0,
                right: 0,
                height: `${100 + parallaxStretch * 200}%`,
                willChange: 'transform',
              }}
            >
              <img
                src={image}
                alt={imageAlt || title}
                className="w-full h-full object-cover"
                style={{ display: 'block', objectPosition: imageObjectPosition, filter: imageFilter }}
              />
            </div>
          )}
        </div>

        {/* Text side — subtle "lead" parallax so it drifts up faster than the
            image, creating a genuine layered depth (Dom-Pérignon style). */}
        <div
          className={`flex items-center reveal px-8 md:px-14 lg:px-20 py-16 md:py-24 ${imageLeft ? 'md:order-2' : 'md:order-1'}`}
          data-parallax-speed="-0.04"
        >
          <div style={{ maxWidth: 560 }}>
            <span className="eyebrow">{eyebrow}</span>
            <h3 className="font-display text-[2rem] md:text-[2.6rem] lg:text-[2.9rem] mt-6 leading-[1.05]" style={{ color: 'var(--lem-ink)' }}>{title}</h3>
            <p className="font-display-italic text-[1.15rem] md:text-[1.3rem] mt-3 inline-flex items-center gap-2" style={{ color: 'var(--lem-terracotta)' }}>
              {subtitleIcon}
              <span>{subtitle}</span>
            </p>
            <p className="font-body text-[1.02rem] md:text-[1.16rem] mt-8 leading-[1.75]" style={{ color: 'var(--lem-ink-soft)' }}>{body}</p>
            {quote && (
              <blockquote className="font-display-italic text-[1.15rem] md:text-[1.3rem] mt-8 pl-5 border-l-2" style={{ borderColor: 'var(--lem-terracotta)', color: 'var(--lem-ink)' }}>
                {quote}
              </blockquote>
            )}
            {linkedin && (
              <div className="soul-actions mt-8 flex items-center gap-5 flex-wrap">
                <a
                  href={linkedin}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="soul-linkedin inline-flex items-center justify-center"
                  data-testid="soul-linkedin-link"
                  aria-label={linkedinLabel}
                  title={linkedinLabel}
                >
                  <Linkedin size={20} />
                </a>
                {onStoryClick ? (
                  <button
                    type="button"
                    onClick={onStoryClick}
                    className="hero-cta hero-cta--ink inline-flex items-center gap-3"
                    data-testid="soul-story-btn"
                  >
                    <span className="hero-cta__line" aria-hidden="true" />
                    <span className="hero-cta__text font-display-italic">
                      {storyLinkLabel || 'La mia storia'}
                    </span>
                    <ChevronRight size={18} className="hero-cta__arrow" />
                  </button>
                ) : storyLink && (
                  <Link
                    to={storyLink}
                    className="hero-cta hero-cta--ink inline-flex items-center gap-3"
                    data-testid="soul-story-link"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <span className="hero-cta__line" aria-hidden="true" />
                    <span className="hero-cta__text font-display-italic">
                      {storyLinkLabel || 'Leggi la sua storia'}
                    </span>
                    <ChevronRight size={18} className="hero-cta__arrow" />
                  </Link>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
